import React, {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';
import {Box, Typography, Card, CardContent, Collapse} from '@mui/material';
import AurButton from '../aur-button/AurButton';
import AurIcon, {IconNames} from '../aur-icon/AurIcon';
import AurSvgIcon from '../aur-svg-icon/AurSvgIcon';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {t} from 'i18next';
import {
  clearNewlyAddedPermissionGroup,
  permissionGroupSelector,
} from '../../../redux/slices/permissionGroupSlice';
import {isNewlyAddedPermissionGroupForClient} from '../../../utils/permissionGroupItemUtils';
import PermissionGroupDialog from '../../permission-groups/PermissionGroupDialog';
import {permissionGroupToItemType} from '../../../utils/permissionGroupItemUtils';
import {
  CLIENT_PERMISSION_CODES_CONSTANTS,
  PERMISSION_GROUP_CATEGORY,
  PERMISSION_TYPE,
} from '../../../constants/constants';
import {getPermissionsbyClientId} from '../../../api/ClientManagementService';
import {Permission} from '../../../types/types';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';
import {setUserRoleForSelectedClient} from '../../../redux/slices/userRoleSlice';
import {UserRoleCode} from '../../../types/user-role-codes/roleCodes';

interface ItemType {
  ItemKey: string;
  ItemName: string;
  ItemDescription: string;
  ItemFeatures: string[];
  ItemFeaturesForECI: string[];
  ItemFeaturesForBudget: string[];
  ItemEvidenceSourceManagement: string[];
  ItemFeaturesForSystem: string[];
  ItemFeaturesForManageProject: string[];
  ItemFeaturesForBusinessIntelligence: string[];
}

interface PermissionGroupSelect {
  label: string;
  subheaderLabel?: string;
  items: ItemType[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  clientId?: string;
  onCreateNewPermissionGroup?: (clientId: string) => void;
  disabledMessage?: string;
  category?: PERMISSION_GROUP_CATEGORY | '';
}

const AurPermissionGroupSelect: React.FC<PermissionGroupSelect> = ({
  label,
  items,
  value,
  onChange,
  disabled = false,
  clientId,
  onCreateNewPermissionGroup,
  subheaderLabel,
  disabledMessage = '',
  category = '',
}) => {
  const dispatch = useDispatch();
  const permissionGroupsState = useSelector(permissionGroupSelector);

  const listItems = useMemo(() => {
    const merged = [...items];

    const matchingFromRedux = (permissionGroupsState.permissionGroups ?? [])
      .filter(
        (group) =>
          (!clientId || group.clientId === clientId) &&
          (!category || group.category === category)
      )
      .map(permissionGroupToItemType);

    for (const groupItem of matchingFromRedux) {
      if (!merged.some((item) => item.ItemKey === groupItem.ItemKey)) {
        merged.push(groupItem);
      }
    }

    if (value && !merged.some((group) => group.ItemKey === value)) {
      const fromRedux = (permissionGroupsState.permissionGroups ?? []).find(
        (group) =>
          group.permissionGroupId === value &&
          (!clientId || group.clientId === clientId)
      );
      if (fromRedux) {
        merged.push(permissionGroupToItemType(fromRedux));
      }
    }

    return merged.sort((a, b) =>
      a.ItemName.toLowerCase().localeCompare(b.ItemName.toLowerCase())
    );
  }, [
    items,
    value,
    permissionGroupsState.permissionGroups,
    clientId,
    category,
  ]);

  const hasGroups = listItems.length > 0;
  const [expanded, setExpanded] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ItemType>();
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isPermissionListLoading, setIsPermissionListLoading] =
    useState<boolean>(false);
  const [
    hasManagePermissionGroupsPermission,
    setHasManagePermissionGroupsPermission,
  ] = useState<boolean>(false);

  const hintID = useId();

  const shouldShowExpandIcon = !value && (disabled || hasGroups);
  const showCreateLink =
    expanded && hasGroups && hasManagePermissionGroupsPermission;

  const newlyAddedPermissionGroupId = useSelector(
    (state: RootState) => state.permissionGroups.newlyAddedPermissionGroupId
  );
  const newlyAddedPermissionGroupCategory = useSelector(
    (state: RootState) =>
      state.permissionGroups.newlyAddedPermissionGroupCategory
  );

  const prevClientIdRef = useRef<string | undefined>(undefined);
  const isInitialClientIdEffectRef = useRef(true);

  const selectedGroupData = listItems.find((group) => group.ItemKey === value);

  const handleSelect = (ItemKey: string) => {
    onChange(ItemKey);
    setExpanded(false);
  };

  const handleCardKeyDown = (e: KeyboardEvent, ItemKey: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(ItemKey);
    }
  };

  const handleExpandToggle = () => setExpanded((prev) => !prev);

  useEffect(() => {
    if (
      !newlyAddedPermissionGroupId ||
      !isNewlyAddedPermissionGroupForClient(permissionGroupsState, clientId) ||
      value === newlyAddedPermissionGroupId ||
      newlyAddedPermissionGroupCategory !== category ||
      disabled
    ) {
      return;
    }

    handleSelect(newlyAddedPermissionGroupId);
  }, [
    newlyAddedPermissionGroupId,
    newlyAddedPermissionGroupCategory,
    permissionGroupsState.newlyAddedPermissionGroupClientId,
    clientId,
    value,
    category,
    disabled,
  ]);

  // Reset selection only when the client changes (not on every parent re-render).
  useEffect(() => {
    if (isInitialClientIdEffectRef.current) {
      isInitialClientIdEffectRef.current = false;
      prevClientIdRef.current = clientId;
      return;
    }
    if (prevClientIdRef.current === clientId) {
      return;
    }
    prevClientIdRef.current = clientId;
    if (!disabled) {
      setExpanded(false);
      onChange('');
      dispatch(clearNewlyAddedPermissionGroup());
    }
  }, [clientId, disabled, dispatch, onChange]);

  useEffect(() => {
    return () => {
      dispatch(clearNewlyAddedPermissionGroup());
    };
  }, [dispatch]);

  useEffect(() => {
    if (clientId) checkPermissions();
  }, [clientId]);

  const checkPermissions = async () => {
    setIsPermissionListLoading(true);
    try {
      const response = await getPermissionsbyClientId(clientId ?? '');

      const permissions = [
        ...((response?.data?.data?.value?.permissions || []) as Permission[]),
        ...((response?.data?.data?.value?.projectPermissions ||
          []) as Permission[]),
      ];

      const userRoles = response?.data?.data?.value?.userRole ?? [];

      const adminRole = userRoles.find(
        (item: any) =>
          item?.role?.roleCode === UserRoleCode.systemAdminUserRole ||
          item?.role?.roleCode === UserRoleCode.clientAdminUserRole
      )?.role;

      const selectedRoleForClient = userRoles.find(
        (item: any) => item?.client?.clientId === clientId
      )?.role;

      const userRoleToSet = adminRole ?? selectedRoleForClient;

      dispatch(setUserRoleForSelectedClient(userRoleToSet?.roleCode ?? ''));

      const existsViewManagePermissionGroups = permissions.some(
        (item) =>
          item.type === PERMISSION_TYPE.SYSTEM &&
          item.permissionCode ===
            CLIENT_PERMISSION_CODES_CONSTANTS.MANAGE_PERMISSION_GROUPS
      );

      setHasManagePermissionGroupsPermission(existsViewManagePermissionGroups);
    } catch (error) {
      console.error(error);
    } finally {
      setIsPermissionListLoading(false);
    }
  };

  const renderContentDetails = (group: ItemType) => (
    <Box className="group-list-card-content-details">
      <Typography className="typo-text subhead3_size item-name">
        {group.ItemName}
      </Typography>
      <Typography className="typo-text body3_size item-description">
        {group.ItemDescription}
      </Typography>
      <AurButton
        classNames="more-details"
        type="text"
        size="Small"
        label={t('addEditTeamMember.form.moreDetails')}
        tabIndex={0}
        ariaLabel={t('common.viewMoreDetailsAbout', {
          itemName: group.ItemName,
        })}
        onClick={(e) => {
          setSelectedGroup(group);
          setShowPermissionModal(true);
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setSelectedGroup(group);
            setShowPermissionModal(true);
            e.stopPropagation();
          }
        }}
      />
    </Box>
  );

  return (
    <Box
      aria-disabled={disabled}
      className={`permission-group-select ${disabled ? 'permission-group-disabled' : ''} ${
        !disabled && !expanded && !value && hasGroups ? 'hover-border' : ''
      }`}
    >
      {/* Header */}
      <Box className="permission-group-select-header">
        <Box className="permission-group-select-header-left">
          {shouldShowExpandIcon && (
            <AurButton
              dataTestId="permission-group-accordion-toggle"
              type="secondary"
              onClick={handleExpandToggle}
              aria-expanded={expanded}
              ariaLabel={`${t(expanded ? 'buttons.collapse' : 'buttons.expand')} ${t('addEditTeamMember.form.permissionGroupWithoutAsterisk')}`}
              startIcon={
                <AurIcon
                  icon={expanded ? IconNames.ChevronUp : IconNames.ChevronDown}
                />
              }
              ariaDescribedBy={hintID}
              disabled={disabled}
              classNames={disabled ? 'expand-icon-disabled' : ''}
            />
          )}
          <Box>
            <Typography
              className={`typo-text subhead4_size ${disabled ? 'heading-color-disabled' : 'heading-color'}`}
            >
              {label}
            </Typography>
            <Typography
              className={`typo-text body3_size ${disabled ? 'heading-color-disabled' : 'subheading-color'}`}
            >
              {subheaderLabel}
            </Typography>
          </Box>
        </Box>
        {/* Right-aligned Create Permission Group Button */}
        {!disabled && !hasGroups && (
          <AurButton
            classNames="typo-text buttonMedium_size create-permission-group-style"
            type="continue"
            size="Medium"
            dataTestId="create-permission-group-button"
            label={t('addEditTeamMember.form.CreatePermissionGroup')}
            onClick={() => onCreateNewPermissionGroup?.(clientId ?? '')}
          />
        )}

        {/* Right-aligned Create Permission Group Link */}
        {showCreateLink && (
          <AurButton
            classNames="create-permission-group-style"
            startIcon={<AurIcon icon={IconNames.Add} />}
            type="text"
            size="Medium"
            dataTestId="create-permission-group-link"
            label={t('addEditTeamMember.form.CreatePermissionGroup')}
            onClick={() => onCreateNewPermissionGroup?.(clientId ?? '')}
          />
        )}

        {value && hasGroups && !expanded && !disabled && (
          <AurButton
            type="secondary"
            size="Medium"
            label={t('buttons.change')}
            onClick={() => setExpanded(true)}
          />
        )}
      </Box>

      {/* Disabled message */}
      {disabled && (
        <Box className="hint hint-disabled">
          <Typography className="typo-text body3_size">
            {disabledMessage
              ? disabledMessage
              : t('addEditTeamMember.form.permissionGroupDisabledMessage')}
          </Typography>
        </Box>
      )}

      {/* No groups message */}
      {!disabled && !hasGroups && (
        <Box className="hint has-no-groups">
          <Typography className="typo-text body3_size">
            {t('addEditTeamMember.form.permissionGroupNoGroupsMessage')}
          </Typography>
        </Box>
      )}

      {/* Summary if selected and collapsed */}
      {!disabled && !expanded && selectedGroupData && (
        <Box className="group-summary">
          <Card variant="outlined" className="group-summary-card">
            <CardContent className="group-summary-card-content">
              <Box className="group-summary-card-content-icon">
                <AurSvgIcon src="../assests/images/complete.svg" />
              </Box>
              {renderContentDetails(selectedGroupData)}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Group List */}
      {hasGroups && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box className="group-list" id="group-list">
            {listItems.map((group) => {
              const isSelected = value === group.ItemKey;
              return (
                <Card
                  key={group.ItemKey}
                  variant="outlined"
                  tabIndex={0}
                  onClick={() => handleSelect(group.ItemKey)}
                  onKeyDown={(e) => handleCardKeyDown(e, group.ItemKey)}
                  className={`group-list-card ${isSelected ? 'selected' : ''}`}
                >
                  <CardContent className="group-list-card-content">
                    <Box className="group-list-card-content-icon">
                      {isSelected && <AurIcon icon={IconNames.CheckMark} />}
                    </Box>
                    {renderContentDetails(group)}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Collapse>
      )}
      {showPermissionModal && (
        <PermissionGroupDialog
          open={showPermissionModal}
          onClose={(e) => {
            setShowPermissionModal(false);
            e.stopPropagation();
          }}
          permissionModalData={selectedGroup}
          permCategory={category || PERMISSION_GROUP_CATEGORY.CLIENT}
        />
      )}

      {<AurLoadingIndicator overlay={true} isOpen={isPermissionListLoading} />}
    </Box>
  );
};

export default AurPermissionGroupSelect;
